import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useRef, useState } from 'react';
import * as Flags from 'country-flag-icons/react/3x2';
import { Captions, Upload, Trash2, Loader2, ChevronDown, ChevronUp, Info, AlertCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { HeroCard } from '@/components/ui/HeroCard';
import { ColorEditorPopover } from './autoplay/ColorEditorPopover';
import { VideoFeatures } from '@/store/useVideoFeatures';
import { SubtitleConfig as SubtitleConfigType, SubtitleTrack } from '@/store/videoFeatures/types/video-features';
import { subtitleService } from '@/services/subtitleService';
import { useToast } from '@/hooks/use-toast';

interface SubtitleConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

const FONT_FAMILIES = [
  { value: 'inherit', get label() { return translateInterfaceText("Padrão do player"); } },
  { value: 'Inter, sans-serif', get label() { return translateInterfaceText("Inter"); } },
  { value: 'Montserrat, sans-serif', get label() { return translateInterfaceText("Montserrat"); } },
  { value: 'Georgia, serif', get label() { return translateInterfaceText("Georgia"); } },
  { value: 'monospace', get label() { return translateInterfaceText("Monospace"); } },
];

const FONT_SIZES = [
  { value: '0.85em', get label() { return translateInterfaceText("Pequeno"); } },
  { value: '1em', get label() { return translateInterfaceText("Médio"); } },
  { value: '1.2em', get label() { return translateInterfaceText("Grande"); } },
  { value: '1.5em', get label() { return translateInterfaceText("Extra grande"); } },
];

const POSITIONS = [
  { value: 'bottom', get label() { return translateInterfaceText("Abaixo"); } },
  { value: 'middle', get label() { return translateInterfaceText("Meio"); } },
  { value: 'top', get label() { return translateInterfaceText("Acima"); } },
];

const ALIGNMENTS = [
  { value: 'left', get label() { return translateInterfaceText("Esquerda"); } },
  { value: 'center', get label() { return translateInterfaceText("Centro"); } },
  { value: 'right', get label() { return translateInterfaceText("Direita"); } },
];

const LANGUAGES = [
  { code: 'pt', countryCode: 'BR', get label() { return translateInterfaceText("Português (BR)"); } },
  { code: 'pt-PT', countryCode: 'PT', get label() { return translateInterfaceText("Português (PT)"); } },
  { code: 'en', countryCode: 'US', get label() { return translateInterfaceText("English"); } },
  { code: 'es', countryCode: 'ES', get label() { return translateInterfaceText("Español"); } },
  { code: 'fr', countryCode: 'FR', get label() { return translateInterfaceText("Français"); } },
  { code: 'de', countryCode: 'DE', get label() { return translateInterfaceText("Deutsch"); } },
  { code: 'it', countryCode: 'IT', get label() { return translateInterfaceText("Italiano"); } },
  { code: 'ja', countryCode: 'JP', label: '日本語' },
  { code: 'ko', countryCode: 'KR', label: '한국어' },
  { code: 'zh', countryCode: 'CN', label: '中文' },
  { code: 'ru', countryCode: 'RU', label: 'Русский' },
  { code: 'ar', countryCode: 'SA', label: 'العربية' },
  { code: 'nl', countryCode: 'NL', get label() { return translateInterfaceText("Nederlands"); } },
  { code: 'pl', countryCode: 'PL', get label() { return translateInterfaceText("Polski"); } },
  { code: 'tr', countryCode: 'TR', get label() { return translateInterfaceText("Türkçe"); } },
  { code: 'other', countryCode: null, get label() { return translateInterfaceText("Outro idioma…"); } },
];

const FlagIcon: React.FC<{ countryCode: string | null }> = ({ countryCode }) => {
  useLanguage();
  if (!countryCode) return <span className="text-[14px]">🌐</span>;
  const Flag = (Flags as Record<string, React.FC<React.SVGProps<SVGSVGElement>>>)[countryCode];
  if (!Flag) return null;
  return <Flag style={{ width: 20, height: 'auto', borderRadius: 2 }} />;
};

export const SubtitleConfig: React.FC<SubtitleConfigProps> = ({ features, setFeature }) => {
  useLanguage();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const videoId = features.videoId;
  const subtitles: SubtitleTrack[] = features.subtitles || [];
  const config: SubtitleConfigType = features.subtitleConfig || {
    showByDefault: false,
    fontSize: '1em',
    fontColor: '#FFFFFF',
    backgroundColor: 'rgba(0,0,0,0.75)',
    textAlign: 'center',
    position: 'bottom',
    fontFamily: 'inherit',
    textShadow: false,
  };

  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingLang, setIsDeletingLang] = useState<string | null>(null);
  const [showStyle, setShowStyle] = useState(false);

  const hasControls = features.showControls ?? false;

  // Form de upload
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadLangCode, setUploadLangCode] = useState('pt');
  const [uploadCustomCode, setUploadCustomCode] = useState('');
  const [uploadIsDefault, setUploadIsDefault] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const isOtherLang = uploadLangCode === 'other';
  const effectiveSrclang = isOtherLang ? uploadCustomCode.toLowerCase().slice(0, 5) : uploadLangCode;

  const updateConfig = (partial: Partial<SubtitleConfigType>) => {
    setFeature('subtitleConfig', { ...config, ...partial });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    if (!uploadLabel) {
      setUploadLabel(file.name.replace(/\.(srt|vtt)$/i, ''));
    }
  };

  const handleUpload = async () => {
    if (!videoId || !selectedFile || !uploadLabel || !effectiveSrclang) {
      toast({ title: translateInterfaceText("Preencha todos os campos antes de enviar."), variant: 'destructive' });
      return;
    }

    setIsUploading(true);
    try {
      const result = await subtitleService.uploadSubtitle(
        videoId,
        selectedFile,
        uploadLabel,
        effectiveSrclang,
        uploadIsDefault
      );

      const updated = subtitles.filter((s) => s.srclang !== effectiveSrclang);
      setFeature('subtitles', [...updated, result.subtitle]);

      setSelectedFile(null);
      setUploadLabel('');
      setUploadLangCode('pt');
      setUploadCustomCode('');
      setUploadIsDefault(false);
      if (fileInputRef.current) fileInputRef.current.value = '';

      toast({ title: translateInterfaceText("Legenda adicionada com sucesso!") });
    } catch {
      toast({ title: translateInterfaceText("Erro ao enviar legenda."), variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (srclang: string) => {
    if (!videoId) return;
    setIsDeletingLang(srclang);
    try {
      await subtitleService.deleteSubtitle(videoId, srclang);
      setFeature('subtitles', subtitles.filter((s) => s.srclang !== srclang));
      toast({ title: translateInterfaceText("Legenda removida.") });
    } catch {
      toast({ title: translateInterfaceText("Erro ao remover legenda."), variant: 'destructive' });
    } finally {
      setIsDeletingLang(null);
    }
  };

  const getLangInfo = (srclang: string) => {
    return LANGUAGES.find(l => l.code === srclang);
  };

  return (
    <div className="space-y-5 py-4 px-6">
      {/* Hero toggle */}
      <HeroCard
        icon={<Captions className="w-full h-full" strokeWidth={2} />}
        title={translateInterfaceText("Legendas")}
        description={translateInterfaceText("Exiba legendas sincronizadas no seu vídeo. Suporta arquivos .SRT e .VTT.")}
        isActive={config.showByDefault}
        onToggle={(val) => updateConfig({ showByDefault: val })}
      />

      {/* Conteúdo condicional: ON = configurações, OFF = warning */}
      {config.showByDefault ? (
        <>
          {/* Aviso quando controles estão desativados */}
          {!hasControls && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
              <Info className="h-4 w-4 text-[#1D4FFF] mt-0.5 shrink-0" />
              <p className="text-[12px] text-blue-700 leading-relaxed">
                {translateInterfaceText("Os")}{" "}<strong>{translateInterfaceText("Controles")}</strong> {" "}{translateInterfaceText("estão desativados. Sem eles, a legenda ficará sempre visível na tela quando ativada e o espectador não terá como desligar.")}</p>
            </div>
          )}

          {/* Tracks existentes */}
          {subtitles.length > 0 && (
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold text-[#374151] uppercase tracking-wide">
                {translateInterfaceText("Legendas adicionadas")}</Label>
              <div className="space-y-2">
                {subtitles.map((sub) => {
                  const lang = getLangInfo(sub.srclang);
                  return (
                    <div
                      key={sub.srclang}
                      className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        {lang && (
                          <FlagIcon countryCode={lang.countryCode} />
                        )}
                        <span className="text-[11px] font-mono font-bold text-[#6B7280] uppercase bg-[#E5E7EB] rounded px-1.5 py-0.5">
                          {sub.srclang}
                        </span>
                        <span className="text-[13px] text-[#111827]">{sub.label}</span>
                        {sub.isDefault && (
                          <span className="text-[10px] text-blue-600 font-semibold">{translateInterfaceText("padrão")}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDelete(sub.srclang)}
                        disabled={isDeletingLang === sub.srclang}
                        className="text-[#9CA3AF] hover:text-red-500 transition-colors disabled:opacity-50"
                      >
                        {isDeletingLang === sub.srclang ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upload de nova legenda */}
          <div className="space-y-3 rounded-xl border border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-4">
            <div className="flex items-center justify-between">
              <Label className="text-[12px] font-semibold text-[#374151] uppercase tracking-wide">
                {translateInterfaceText("Adicionar legenda")}</Label>
              <span className="text-[13px] font-semibold text-[#2563EB]">
                {translateInterfaceText("Uma legenda por idioma")}</span>
            </div>

            {/* Área de seleção de arquivo */}
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] bg-white py-5 cursor-pointer hover:border-blue-400 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-5 w-5 text-[#9CA3AF]" />
              <p className="text-[13px] text-[#6B7280]">
                {selectedFile ? selectedFile.name : translateInterfaceText("Clique para selecionar .SRT ou .VTT")}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".srt,.vtt"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Nome da legenda */}
              <div className="space-y-1">
                <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Nome da legenda")}</Label>
                <Input
                  placeholder={translateInterfaceText("ex: Português")}
                  value={uploadLabel}
                  onChange={(e) => setUploadLabel(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              {/* Idioma com bandeira */}
              <div className="space-y-1">
                <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Idioma")}</Label>
                <Select value={uploadLangCode} onValueChange={setUploadLangCode}>
                  <SelectTrigger className="h-8 text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code} className="text-[12px] py-1.5">
                        <span className="flex items-center gap-2">
                          <FlagIcon countryCode={lang.countryCode} />
                          <span>{lang.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Campo extra para idioma personalizado */}
            {isOtherLang && (
              <div className="space-y-1">
                <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Código do idioma (ex: pt, en, es)")}</Label>
                <Input
                  placeholder={translateInterfaceText("ex: pt-BR, zh-TW")}
                  value={uploadCustomCode}
                  onChange={(e) => setUploadCustomCode(e.target.value.toLowerCase().slice(0, 5))}
                  className="h-8 text-sm"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={uploadIsDefault}
                  onCheckedChange={setUploadIsDefault}
                  id="upload-default"
                />
                <Label htmlFor="upload-default" className="text-[12px] text-[#374151] cursor-pointer">
                  {translateInterfaceText("Exibir esta primeiro")}</Label>
              </div>
              <Button
                size="sm"
                onClick={handleUpload}
                disabled={isUploading || !selectedFile || !effectiveSrclang}
                className="h-8 text-[13px]"
              >
                {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                {isUploading ? translateInterfaceText("Enviando...") : translateInterfaceText("Adicionar")}
              </Button>
            </div>
          </div>

          {/* Estilo das legendas (colapsável) */}
          <div className="rounded-xl border border-[#E5E7EB]">
            <button
              className="flex w-full items-center justify-between px-4 py-3 text-[13px] font-semibold text-[#374151]"
              onClick={() => setShowStyle((v) => !v)}
            >
              {translateInterfaceText("Estilo das legendas")}{showStyle ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {showStyle && (
              <div className="space-y-4 border-t border-[#E5E7EB] px-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Cor do texto")}</Label>
                    <ColorEditorPopover
                      colorName={translateInterfaceText("Texto")}
                      currentColor={config.fontColor}
                      onSave={(c) => updateConfig({ fontColor: c })}
                    >
                      <div className="flex h-8 cursor-pointer items-center gap-2 rounded-md border border-[#E5E7EB] px-2">
                        <div
                          className="h-4 w-4 rounded-sm border border-[#E5E7EB]"
                          style={{ backgroundColor: config.fontColor }}
                        />
                        <span className="text-[12px] text-[#374151]">{config.fontColor}</span>
                      </div>
                    </ColorEditorPopover>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Cor do fundo")}</Label>
                    <ColorEditorPopover
                      colorName={translateInterfaceText("Fundo")}
                      currentColor={config.backgroundColor}
                      onSave={(c) => updateConfig({ backgroundColor: c })}
                    >
                      <div className="flex h-8 cursor-pointer items-center gap-2 rounded-md border border-[#E5E7EB] px-2">
                        <div
                          className="h-4 w-4 rounded-sm border border-[#E5E7EB]"
                          style={{ backgroundColor: config.backgroundColor }}
                        />
                        <span className="text-[12px] text-[#374151] truncate">{config.backgroundColor}</span>
                      </div>
                    </ColorEditorPopover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Tamanho")}</Label>
                    <Select value={config.fontSize} onValueChange={(v) => updateConfig({ fontSize: v })}>
                      <SelectTrigger className="h-8 text-[12px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_SIZES.map((f) => (
                          <SelectItem key={f.value} value={f.value} className="text-[12px] py-1.5">
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Fonte")}</Label>
                    <Select value={config.fontFamily} onValueChange={(v) => updateConfig({ fontFamily: v })}>
                      <SelectTrigger className="h-8 text-[12px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_FAMILIES.map((f) => (
                          <SelectItem key={f.value} value={f.value} className="text-[12px] py-1.5">
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Posição")}</Label>
                    <Select value={config.position} onValueChange={(v) => updateConfig({ position: v as any })}>
                      <SelectTrigger className="h-8 text-[12px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value} className="text-[12px] py-1.5">
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Alinhamento")}</Label>
                    <Select value={config.textAlign} onValueChange={(v) => updateConfig({ textAlign: v as any })}>
                      <SelectTrigger className="h-8 text-[12px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALIGNMENTS.map((a) => (
                          <SelectItem key={a.value} value={a.value} className="text-[12px] py-1.5">
                            {a.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={config.textShadow}
                    onCheckedChange={(v) => updateConfig({ textShadow: v })}
                    id="text-shadow"
                  />
                  <Label htmlFor="text-shadow" className="text-[12px] text-[#374151] cursor-pointer">
                    {translateInterfaceText("Sombra no texto")}</Label>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Warning quando o recurso está desativado */
        <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-[#6B7280] mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-[13px] font-semibold text-[#374151]">{translateInterfaceText("Como funciona o recurso de legendas")}</p>
              <p className="text-[12px] text-[#6B7280] leading-relaxed">
                {translateInterfaceText("Com as legendas ativadas, você pode adicionar arquivos")}{" "}<strong>{translateInterfaceText(".SRT")}</strong> {" "}{translateInterfaceText("ou")}{" "}<strong>{translateInterfaceText(".VTT")}</strong> {" "}{translateInterfaceText("ao seu vídeo. O espectador poderá ativar ou desativar a legenda pelo botão")}{" "}<strong>{translateInterfaceText("CC")}</strong> {" "}{translateInterfaceText("nos controles.")}</p>
            </div>
          </div>

          <p className="text-[11px] text-[#9CA3AF] leading-relaxed">
            {translateInterfaceText("Ative o switch acima para começar a configurar suas legendas.")}</p>
        </div>
      )}
    </div>
  );
};
